import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Chart } from 'primereact/chart';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Knob } from 'primereact/knob';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Skeleton } from 'primereact/skeleton';
import { getUserDashboardKpi, getUserDashboardTrend, getUserDashboardActivity } from '../services/userDashboardService';

// ─── KPI Card — icon + kebab + value + share ring ────────────────────────────

const KpiCard = ({ title, icon, color, data, loading, total, onClick }) => {
    const value = data?.this_month ?? 0;
    const changePct = data?.change_pct;
    const isPositive = changePct > 0;
    const isNegative = changePct < 0;
    // Ring fills by this stage's share of the month's total pipeline, and shows
    // the actual count (this month) in its centre.
    const ringMax = total > 0 ? total : Math.max(value, 1);

    return (
        <div className="col-6 lg:col-2">
            <Card
                className="kpi-card h-full shadow-1 cursor-pointer hover:shadow-3 transition-all transition-duration-200"
                /* Body padding comes from the .kpi-card rule in the grid below —
                   PrimeFlex has no utility between p-2 (8px) and p-3 (16px). */
                pt={{ content: { className: 'p-0' } }}
                onClick={onClick}
            >
                {loading ? (
                    <>
                        <div className="flex justify-content-between align-items-center mb-3">
                            <Skeleton shape="circle" size="2.5rem" />
                            <Skeleton width="4rem" height="0.9rem" />
                        </div>
                        <div className="flex justify-content-between align-items-center">
                            <div className="flex flex-column gap-2">
                                <Skeleton width="4rem" height="0.7rem" />
                                <Skeleton width="3rem" height="0.7rem" />
                                <Skeleton width="3rem" height="0.7rem" />
                            </div>
                            <Skeleton shape="circle" size="3.9rem" />
                        </div>
                    </>
                ) : (
                    <>
                        {/* Top row — icon circle (left) + title (right) */}
                        <div className="flex justify-content-between align-items-center gap-2">
                            <div
                                className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                                style={{ width: '2.5rem', height: '2.5rem', backgroundColor: `var(--${color}-500)` }}
                            >
                                <i className={`pi ${icon}`} style={{ color: `var(--${color}-50)` }} aria-hidden="true" />
                            </div>
                            <span
                                className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis"
                                title={title}
                            >
                                {title}
                            </span>
                        </div>

                        {/* Bottom row — Week/Prev stats (left) + "this month" over the ring (right) */}
                        <div className="flex justify-content-between align-items-center mt-2 gap-2">
                            <div className="flex flex-column gap-2 pt-3" style={{ minWidth: 0 }}>
                                <span className="text-500 text-xs white-space-nowrap">Week <strong className="text-700">{data?.this_week ?? 0}</strong></span>
                                <span className="text-500 text-xs white-space-nowrap">Prev <strong className="text-700">{data?.last_month ?? 0}</strong></span>
                                {changePct != null && (
                                    <span
                                        className={`text-xs font-medium white-space-nowrap ${isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-500'}`}
                                        title="vs last month"
                                    >
                                        <i className={`pi ${isPositive ? 'pi-arrow-up' : isNegative ? 'pi-arrow-down' : 'pi-minus'}`} style={{ fontSize: '0.6rem' }} aria-hidden="true" />
                                        {' '}{Math.abs(changePct)}%
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-column align-items-center gap-1 flex-shrink-0">
                                {/* "this month" sits above the ring */}
                                <span className="text-600 font-medium white-space-nowrap" style={{ fontSize: '0.8125rem' }}>This month</span>
                                <Knob
                                    value={value}
                                    min={0}
                                    max={ringMax}
                                    readOnly
                                    size={40}
                                    strokeWidth={8}
                                    valueColor={`var(--${color}-500)`}
                                    rangeColor={`var(--${color}-100)`}
                                    textColor={`var(--${color}-700)`}
                                    valueTemplate="{value}"
                                />
                            </div>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const UserDashboard = () => {
    const navigate = useNavigate();
    const toast = useRef(null);

    const [kpiData, setKpiData] = useState(null);
    const [kpiLoading, setKpiLoading] = useState(true);

    const [trendData, setTrendData] = useState(null);
    const [trendLoading, setTrendLoading] = useState(true);

    const [activityData, setActivityData] = useState([]);
    const [activityLoading, setActivityLoading] = useState(true);

    const loadKpi = useCallback(async () => {
        setKpiLoading(true);
        try {
            const res = await getUserDashboardKpi();
            if (res.success) setKpiData(res.data);
        } catch (e) {
            console.error('KPI load failed:', e);
        } finally {
            setKpiLoading(false);
        }
    }, []);

    const loadTrend = useCallback(async () => {
        setTrendLoading(true);
        try {
            const res = await getUserDashboardTrend();
            if (res.success) setTrendData(res.data);
        } catch (e) {
            console.error('Trend load failed:', e);
        } finally {
            setTrendLoading(false);
        }
    }, []);

    const loadActivity = useCallback(async () => {
        setActivityLoading(true);
        try {
            const res = await getUserDashboardActivity(15);
            if (res.success) setActivityData(res.data);
        } catch (e) {
            console.error('Activity load failed:', e);
        } finally {
            setActivityLoading(false);
        }
    }, []);

    useEffect(() => { loadKpi(); loadTrend(); loadActivity(); }, [loadKpi, loadTrend, loadActivity]);

    const refreshAll = () => { loadKpi(); loadTrend(); loadActivity(); };

    // ─── Alert badges ────────────────────────────────────────────────────────

    const renderAlertBadges = () => {
        const alerts = kpiData?.alerts;
        if (!alerts) return null;
        const { needs_attention, pending_claims_on_my_leads, my_pending_claims } = alerts;
        if (!needs_attention && !pending_claims_on_my_leads && !my_pending_claims) return null;

        return (
            <div className="flex gap-2 mb-3 flex-wrap">
                {needs_attention > 0 && (
                    <div
                        className="flex align-items-center gap-2 px-3 py-2 border-round cursor-pointer hover:surface-hover"
                        style={{ background: 'var(--orange-50)', border: '1px solid var(--orange-200)' }}
                        onClick={() => navigate('/app/leads/available-offers')}
                    >
                        <i className="pi pi-exclamation-triangle text-orange-600 text-sm" />
                        <span className="text-orange-700 font-semibold text-sm">Needs Attention</span>
                        <Tag value={needs_attention} severity="warning" style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem' }} />
                    </div>
                )}
                {pending_claims_on_my_leads > 0 && (
                    <div
                        className="flex align-items-center gap-2 px-3 py-2 border-round cursor-pointer hover:surface-hover"
                        style={{ background: 'var(--yellow-50)', border: '1px solid var(--yellow-200)' }}
                        onClick={() => navigate('/app/leads/received-claims?tab=received')}
                    >
                        <i className="pi pi-inbox text-yellow-700 text-sm" />
                        <span className="text-yellow-800 font-semibold text-sm">Pending Approvals</span>
                        <Tag value={pending_claims_on_my_leads} severity="warning" style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem' }} />
                    </div>
                )}
                {my_pending_claims > 0 && (
                    <div
                        className="flex align-items-center gap-2 px-3 py-2 border-round cursor-pointer hover:surface-hover"
                        style={{ background: 'var(--blue-50)', border: '1px solid var(--blue-200)' }}
                        onClick={() => navigate('/app/leads/received-claims?tab=sent')}
                    >
                        <i className="pi pi-clock text-blue-600 text-sm" />
                        <span className="text-blue-700 font-semibold text-sm">My Pending Claims</span>
                        <Tag value={my_pending_claims} severity="info" style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem' }} />
                    </div>
                )}
            </div>
        );
    };

    // ─── Chart ───────────────────────────────────────────────────────────────

    const getChartData = () => {
        if (!trendData) return null;
        return {
            labels: trendData.weeks,
            datasets: [
                {
                    label: 'Submitted',
                    data: trendData.submitted,
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59,130,246,0.1)',
                    tension: 0.3,
                    fill: true,
                },
                {
                    label: 'Approved',
                    data: trendData.approved,
                    borderColor: '#22c55e',
                    backgroundColor: 'rgba(34,197,94,0.1)',
                    tension: 0.3,
                    fill: true,
                },
            ],
        };
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, pointStyle: 'circle', font: { size: 11 } } } },
        scales: {
            y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 10 } }, grid: { color: 'rgba(0,0,0,0.05)' } },
            x: { ticks: { font: { size: 10 } }, grid: { display: false } },
        },
    };

    // ─── Activity feed ───────────────────────────────────────────────────────

    const getActivityDotColor = (status) => {
        if (['sanction', 'disbursal'].includes(status)) return 'var(--green-500)';
        if (['bank_rejected', 'customer_rejected', 'processor_rejected'].includes(status)) return 'var(--red-500)';
        if (['bank_submit', 'bank_processing'].includes(status)) return 'var(--blue-500)';
        if (['broadcasted'].includes(status)) return 'var(--purple-500)';
        if (['processing', 'bank_queries', 'sanction_queries', 'disbursal_queries'].includes(status)) return 'var(--yellow-600)';
        if (['closed'].includes(status)) return 'var(--surface-500)';
        return 'var(--surface-400)';
    };

    // ─── Render (P20 canonical page skeleton) ────────────────────────────────

    // Total pipeline this month — used so each KPI ring shows its share (0–100%).
    const kpi = kpiData?.kpi || {};
    const totalThisMonth = ['new_leads', 'processing', 'at_bank', 'sanctioned', 'disbursed', 'rejected']
        .reduce((sum, k) => sum + (kpi[k]?.this_month || 0), 0);

    return (
        <>
            <Toast ref={toast} position="top-right" />

            <div className="grid">
                <div className="col-12">
                    {/* Page header (P6) */}
                    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
                        <div>
                            <h1 className="text-xl font-semibold text-900 m-0 mb-1">My Dashboard</h1>
                            <p className="text-600 text-sm m-0">Your personal pipeline — KPIs, weekly trend and recent activity</p>
                        </div>
                        <Button
                            icon="pi pi-refresh"
                            label="Refresh"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={refreshAll}
                            aria-label="Refresh dashboard"
                        />
                    </div>

                    {/* KPI Cards — 2 per row on mobile, all six in one row on desktop (lg+). */}
                    {/* Card padding is breakpoint-scaled: at lg the six cards share
                        one row at col-2, so the inner box is only ~150-185px and a
                        full 1rem inset would push "This month" + the ring past the
                        edge. Mobile is 2-up and has the room. !important is needed
                        because both the Card theme and PrimeFlex utilities carry
                        it — this rule has to outrank them. */}
                    <style>{`
                        .kpi-card .p-card-body { padding: 18px !important; }
                        @media screen and (min-width: 992px) {
                            .kpi-card .p-card-body { padding: 10px !important; }
                        }
                    `}</style>
                    <div className="grid mb-3">
                        <KpiCard title="New Leads" icon="pi-briefcase" color="blue" data={kpiData?.kpi?.new_leads} loading={kpiLoading} total={totalThisMonth} onClick={() => navigate('/app/leads')} />
                        <KpiCard title="Processing" icon="pi-spin pi-spinner" color="yellow" data={kpiData?.kpi?.processing} loading={kpiLoading} total={totalThisMonth} onClick={() => navigate('/app/leads?status=processing')} />
                        <KpiCard title="At Bank" icon="pi-building" color="cyan" data={kpiData?.kpi?.at_bank} loading={kpiLoading} total={totalThisMonth} onClick={() => navigate('/app/leads?status=bank_submit')} />
                        <KpiCard title="Sanctioned" icon="pi-check-circle" color="green" data={kpiData?.kpi?.sanctioned} loading={kpiLoading} total={totalThisMonth} onClick={() => navigate('/app/leads?status=sanction')} />
                        <KpiCard title="Disbursed" icon="pi-wallet" color="purple" data={kpiData?.kpi?.disbursed} loading={kpiLoading} total={totalThisMonth} onClick={() => navigate('/app/leads?status=disbursal')} />
                        <KpiCard title="Rejected" icon="pi-times-circle" color="red" data={kpiData?.kpi?.rejected} loading={kpiLoading} total={totalThisMonth} onClick={() => navigate('/app/leads?status=bank_rejected')} />
                    </div>

                    {/* Alert Badges */}
                    {renderAlertBadges()}

                    {/* Chart + Activity */}
                    <div className="grid">
                        <div className="col-12 lg:col-6">
                            <Card className="shadow-1 h-full" pt={{ body: { className: 'p-3' }, content: { className: 'p-0' } }}>
                                <div className="flex align-items-center gap-2 mb-3">
                                    <i className="pi pi-chart-line text-primary" />
                                    <span className="text-900 font-semibold text-sm">Weekly Trend</span>
                                </div>
                                {trendLoading ? (
                                    <div className="flex justify-content-center py-4"><ProgressSpinner style={{ width: '2rem', height: '2rem' }} /></div>
                                ) : trendData ? (
                                    <div style={{ height: '200px' }}>
                                        <Chart type="line" data={getChartData()} options={chartOptions} style={{ height: '100%' }} />
                                    </div>
                                ) : (
                                    <div className="text-center text-500 py-4 text-sm">No trend data yet</div>
                                )}
                            </Card>
                        </div>
                        <div className="col-12 lg:col-6">
                            <Card className="shadow-1 h-full" pt={{ body: { className: 'p-3' }, content: { className: 'p-0' } }}>
                                <div className="flex align-items-center gap-2 mb-3">
                                    <i className="pi pi-clock text-primary" />
                                    <span className="text-900 font-semibold text-sm">Recent Activity</span>
                                </div>
                                {activityLoading ? (
                                    <div className="flex justify-content-center py-4"><ProgressSpinner style={{ width: '2rem', height: '2rem' }} /></div>
                                ) : activityData.length > 0 ? (
                                    <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                        {activityData.map((item) => (
                                            <div key={item.id} className="flex align-items-start gap-2 py-2 border-bottom-1 surface-border">
                                                <div className="flex-shrink-0 mt-1 border-circle" style={{ width: '8px', height: '8px', backgroundColor: getActivityDotColor(item.status) }} />
                                                <div className="flex-1" style={{ minWidth: 0 }}>
                                                    <div className="text-700 text-sm" style={{ lineHeight: '1.3' }}>
                                                        <strong>{item.lead_id_formatted}</strong> — {item.status_label}
                                                        {item.bank_name && <span> · {item.bank_name}</span>}
                                                        {item.rejection_reason_label && <span className="text-red-600"> — {item.rejection_reason_label}</span>}
                                                    </div>
                                                    <div className="text-400" style={{ fontSize: '0.65rem' }}>{item.updated_by} · {item.created_at_human}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center text-500 py-4 text-sm">No activity yet</div>
                                )}
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UserDashboard;
